// StepCanvasSignature — 簽名證據 step。
// Spec: docs/specs/location-tasks.md §8.6.
//
// Canvas captures pointer / touch events; on submit we toBlob('image/png'),
// upload via POST /v1/uploads/signature, then PATCH the submission with
// { signature_r2_key } — JUST the evidence field. Status transition
// (in_progress → submitted) is owned by WizardRunner.handleSubmit so the
// commit point is single + decoupled from any specific evidence type.
// Signature 跟 text / photo / file 同等：都是某些任務需要的證據之一，
// 不該決定整筆 submission 的生命週期。
//
// Stroke heuristic for "empty signature": total path-length below 80 px
// or fewer than 5 distinct points → reject. The threshold is tuned for
// 360-px wide canvas on phone (≈ 8px/mm).

import React, { useEffect, useRef, useState } from "react";
import { PenLine, Loader2, AlertTriangle, RotateCcw, Check } from "lucide-react";
import { apiFetch } from "../../../lib/api-client.js";
import type { WizardCtx, WizardData } from "./types.js";

interface Props {
  ctx: WizardCtx;
  data: WizardData;
  onComplete: () => void;
}

const CANVAS_W = 600;
const CANVAS_H = 220;
const MIN_PATH_PX = 80;
const MIN_POINTS = 5;

export default function StepCanvasSignature({ ctx, data, onComplete }: Props): React.ReactElement {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [empty, setEmpty] = useState(true);

  const drawingRef = useRef(false);
  const pathLenRef = useRef(0);
  const pointCountRef = useRef(0);
  const lastPtRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = CANVAS_W * dpr;
    canvas.height = CANVAS_H * dpr;
    canvas.style.width = `${CANVAS_W}px`;
    canvas.style.height = `${CANVAS_H}px`;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#fafafa";
    ctx.lineWidth = 2.4;
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  }, []);

  const getXY = (e: PointerEvent | React.PointerEvent): { x: number; y: number } => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    // The canvas has two coordinate systems that disagree on phones:
    //   - the drawing context (post `ctx.scale(dpr, dpr)`) thinks in
    //     CANVAS_W × CANVAS_H logical units (600 × 220);
    //   - the rendered <canvas> is clamped by `maxWidth: 100%` to the
    //     phone's container width (typically ~390 px), so rect.width
    //     ≠ CANVAS_W. PointerEvent.clientX is in CSS / display space.
    // Without scaling, a touch at display x=200 draws at logical x=200,
    // which the browser then re-scales onto the rendered canvas at
    // ~200 × (rect.width / CANVAS_W) ≈ 130 px — the offset the operator
    // sees ("觸控點下去畫的位置跟實際畫出來的位置不同"). Map both axes
    // back into logical space so the stroke lands under the finger.
    const scaleX = CANVAS_W / rect.width;
    const scaleY = CANVAS_H / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const onDown: React.PointerEventHandler<HTMLCanvasElement> = (e) => {
    e.preventDefault();
    canvasRef.current?.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const { x, y } = getXY(e);
    lastPtRef.current = { x, y };
    pointCountRef.current += 1;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const onMove: React.PointerEventHandler<HTMLCanvasElement> = (e) => {
    if (!drawingRef.current) return;
    const { x, y } = getXY(e);
    const last = lastPtRef.current;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx || !last) return;
    const dx = x - last.x; const dy = y - last.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 1) return;
    pathLenRef.current += dist;
    pointCountRef.current += 1;
    ctx.lineTo(x, y);
    ctx.stroke();
    lastPtRef.current = { x, y };
    if (empty) setEmpty(false);
  };

  const onUp: React.PointerEventHandler<HTMLCanvasElement> = (e) => {
    drawingRef.current = false;
    canvasRef.current?.releasePointerCapture(e.pointerId);
    lastPtRef.current = null;
  };

  const reset = (): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    pathLenRef.current = 0;
    pointCountRef.current = 0;
    lastPtRef.current = null;
    drawingRef.current = false;
    setEmpty(true);
    setError(null);
  };

  const submit = async (): Promise<void> => {
    if (!canvasRef.current) return;
    if (pathLenRef.current < MIN_PATH_PX || pointCountRef.current < MIN_POINTS) {
      setError("簽名似乎太簡略，請重新簽完整的姓名。");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvasRef.current!.toBlob((b) => resolve(b), "image/png", 0.92),
      );
      if (!blob) throw new Error("toBlob_failed");
      const form = new FormData();
      form.set("file", new File([blob], `signature-${Date.now()}.png`, { type: "image/png" }));
      form.set("submission_id", data.submission.id);
      const up = await apiFetch(ctx.paths.uploadSignature(), { method: "POST", body: form }, ctx.auth);
      const upBody = (await up.json().catch(() => ({}))) as { ok?: boolean; r2_key?: string; error?: string };
      if (!up.ok || !upBody.r2_key) {
        setError(upBody.error ?? `HTTP ${up.status}`);
        return;
      }
      // PATCH submission with signature ONLY — status transition is owned
      // by WizardRunner.handleSubmit (single commit point).
      const fin = await apiFetch(
        ctx.paths.patchSubmission(data.submission.id),
        {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ signature_r2_key: upBody.r2_key }),
        },
        ctx.auth,
      );
      const finBody = (await fin.json().catch(() => ({}))) as { ok?: boolean; finalize?: boolean; error?: string };
      if (!fin.ok) {
        setError(finBody.error ?? `HTTP ${fin.status}`);
        return;
      }
      onComplete();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    // select-none + iOS callout disabled at the step root: signature
    // capture means the operator drags across the area with intent,
    // and any text under their finger triggering the system text-
    // selection / share / lookup callout breaks the flow. Tailwind
    // `select-none` covers user-select; webkit-touch-callout is iOS-
    // specific so we set it inline.
    <div
      className="space-y-2 select-none"
      style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none" }}
    >
      <div className="rounded-lg overflow-hidden border border-border">
        <canvas
          ref={canvasRef}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onPointerLeave={onUp}
          className="block touch-none w-full"
          style={{ touchAction: "none", maxWidth: "100%" }}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={reset}
          disabled={busy || empty}
          className="h-11 rounded-xl bg-bg-raised text-text-primary text-sm font-medium disabled:text-text-muted inline-flex items-center justify-center gap-1"
        >
          <RotateCcw className="h-4 w-4" /> 重簽
        </button>
        <button
          onClick={submit}
          disabled={busy || empty}
          className="h-11 rounded-xl bg-accent text-text-inverse font-semibold text-sm disabled:bg-bg-raised disabled:text-text-muted inline-flex items-center justify-center gap-1"
        >
          {busy ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> 送出中…</>
          ) : (
            <><Check className="h-4 w-4" /> 送出簽名 + 完成</>
          )}
        </button>
      </div>
      {error && (
        <p className="text-xs text-danger inline-flex items-start gap-1">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />{error}
        </p>
      )}
      <p className="text-2xs text-text-muted inline-flex items-center gap-1">
        <PenLine className="h-3 w-3" /> 送出後此任務會立即提交給管理員審核。
      </p>
    </div>
  );
}
